import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import vvaLogo from '../assets/vva-logo.png'; 

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Solutions', href: '/#solutions' },
    { name: 'About', href: '/#about' },
    { name: 'Contact', href: '/#contact' },
  ];

  const handleNavClick = (href) => {
    if (href.startsWith('/#')) {
      if (location.pathname !== '/') {
        window.location.href = href;
      } else {
        const id = href.replace('/#', '');
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const handleGetStarted = () => handleNavClick('/#contact');

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'glass-effect shadow-xl py-2' : 'bg-transparent py-4'
      }`}
    >
      <nav className="container mx-auto px-4 md:px-8 flex items-center justify-between">
        
        {/* --- BRAND IDENTITY SECTION --- */}
        <Link
          to="/"
          className="flex items-center gap-4 group cursor-pointer"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <img 
            src={vvaLogo} 
            alt="VegaVelox Emblem" 
            className="h-10 md:h-12 w-auto object-contain brand-logo-img" 
          />
          <span className="brand-title gradient-text text-xl md:text-2xl font-bold tracking-wide pt-1">
            VEGAVELOX ANALYTECH
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center space-x-8">


          {navItems.map((item, index) => (
            <button
              key={item.name}
              onClick={() => handleNavClick(item.href)}
              className="text-gray-300 hover:text-white transition-colors duration-200 font-medium"
            >
              {item.name}
            </button>
          ))}
          
          <Button
            onClick={handleGetStarted}
            className="bg-[hsl(var(--primary-gold))] text-black px-6 py-2 rounded-lg font-bold transition-all duration-300 hover:brightness-110 shadow-lg hover:shadow-yellow-500/20"
          >
            Get Started
          </Button>
        </div>

        {/* Mobile menu button */}
        <div className="md:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-white"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden overflow-hidden bg-[#1c1d21]/95 backdrop-blur-xl border-b border-[hsl(var(--border))]/50"
          >
            <div className="p-4 space-y-2">

              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item.href)}
                  className="block w-full text-left py-3 text-gray-300 hover:text-white transition-colors"
                >
                  {item.name}
                </button>
              ))}
              <Button
                onClick={handleGetStarted}
                className="w-full mt-4 bg-[hsl(var(--primary-gold))] text-black font-bold"
              >
                Get Started
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;